// frontend/src/navigation/sidebarPrefs.js

/**
 * sidebarPrefs — der PERSOENLICHE Geltungsbereich der Navigation (W-21).
 *
 * @description
 *   Atlassian faehrt zwei Geltungsbereiche, und die Trennung ist die eigentliche
 *   Idee: „frequency-shaped organisation belongs to the individual, shared
 *   vocabulary to the team."
 *
 *     GETEILT (admin-eigen)   Abschnitte, Beschriftungen, Symbole, Reihenfolge
 *                             der Abschnitte. Der Wortschatz, auf den sich alle
 *                             einigen muessen. Hier aendert der Nutzer NICHTS.
 *     PERSOENLICH (hier)      welche Eintraege sichtbar sind, in welcher
 *                             Reihenfolge sie innerhalb ihres Abschnitts stehen,
 *                             und welche angeheftet (Favoriten) sind.
 *
 *   Das ist der ganze Schnitt. Wer ihn aufweicht — etwa Beschriftungen
 *   editierbar macht — verliert den Grund, warum es zwei Bereiche gibt: dann
 *   redet jeder ueber eine andere Seite.
 *
 * 🔴 DIE INVARIANTE, und sie ist der Grund, warum diese Datei ueberhaupt Regeln
 *   hat: ein Abschnitt behaelt IMMER seinen Standard-Eintrag (den ersten, so wie
 *   die Verwaltung ihn definiert hat). Ohne die Regel laesst sich die Leiste auf
 *   einen leeren Rumpf reduzieren — die Abschnittsueberschrift bliebe stehen,
 *   darunter nichts, und es gaebe keinen Weg zurueck ausser die Einstellungen zu
 *   finden, die man gerade weggeklickt hat. Atlassian formuliert dasselbe fuer
 *   Tabs: „You're unable to remove a tab that has been set as the default. A
 *   space must always have a default tab."
 *
 * 🔴 DREI SAETZE, die den Favoriten-Teil festlegen (Plan §3.1, B2). Sie stehen
 *   hier, weil jede fuer sich eine Falle ausschliesst:
 *
 *   1. `favoriten[]` ist die EINZIGE Quelle der Favoriten-Reihenfolge. Das Array
 *      selbst IST die Ordnung; es gibt keinen Schluessel `favoriten` in
 *      `reihenfolge`. Zwei Wohnsitze fuer dieselbe Ordnung waeren zwei
 *      Wahrheiten, die auseinanderlaufen.
 *   2. `wendeAn` (und damit `sortiereNachPrefs` und `sichtbarePfade`) wird auf den
 *      Favoriten-Abschnitt NIE angewandt. Er ist kein Objekt mit `items`, sondern
 *      eine Liste von Pfaden, die ueber `navItemSicher` zu Zeilen wird. Ein
 *      Abschnittsobjekt mit `items: []` ergaebe ueber `wendeAn` IMMER eine leere
 *      Liste — genau diese Falle schliesst der Satz aus.
 *   3. `verschiebeInFavoriten` schreibt fuer Favoriten, `verschiebeInAbschnitt`
 *      fuer Abschnitte. Es gibt keine ueberladene Funktion, die beide Faelle ueber
 *      eine `sectionId` unterscheidet — die zwei Faelle sind an zwei Namen
 *      gebunden und damit an zwei Tests.
 *
 * 🔴 `verschiebe()` (hoch/runter) ist in PR B GEFALLEN, zusammen mit der zweiten
 *   Liste der Seitenleiste, ihrem einzigen Aufrufer (Plan §4 S3, B5). An ihre
 *   Stelle tritt `verschiebeInAbschnitt` mit denselben Randfaellen — die
 *   Reihenfolge aendert sich jetzt per Zug, nicht mehr per Pfeil.
 */

const SCHLUESSEL = 'forge.sidebarPrefs'

/**
 * Die Abschnitts-Id der Favoritenliste.
 *
 * Sie ist eine Konstante und kein Literal an drei Stellen: derselbe Wert ist die
 * `droppableId` der Favoritenliste im Modus (Plan §3.3) und das Argument, das
 * `wendeZugAn` fuer „Ziel ist die Favoritenliste" erkennt.
 */
export const FAVORITEN = 'favoriten'

/** Leere Voreinstellung — „nichts versteckt, nichts umsortiert, nichts angeheftet". */
export const LEER = { versteckt: [], reihenfolge: {}, favoriten: [] }

/**
 * Liest die Voreinstellung aus dem Speicher — und ist gegen Altdaten tolerant.
 *
 * 🔴 Die drei `Array.isArray`-Pruefungen sind kein Zierrat: ohne sie risse ein
 *   Eintrag, der vor dem Favoriten-Feld geschrieben wurde (oder ein von Hand
 *   veraenderter Wert), die Leiste in den Error-Boundary. Fehlt `favoriten`,
 *   liefert die Funktion `[]` — der Nutzer verliert nichts, was er je hatte.
 */
export function holePrefs() {
  try {
    const roh = window.localStorage.getItem(SCHLUESSEL)
    if (!roh) return LEER
    const p = JSON.parse(roh)
    return {
      versteckt: Array.isArray(p?.versteckt) ? p.versteckt.filter(x => typeof x === 'string') : [],
      reihenfolge: (p?.reihenfolge && typeof p.reihenfolge === 'object') ? p.reihenfolge : {},
      favoriten: Array.isArray(p?.favoriten) ? p.favoriten.filter(x => typeof x === 'string') : [],
    }
  } catch {
    return LEER
  }
}

export function merkePrefs(prefs) {
  try { window.localStorage.setItem(SCHLUESSEL, JSON.stringify(prefs)) } catch { /* Bequemlichkeit */ }
}

/**
 * Ist dieser Eintrag der Standard seines Abschnitts?
 *
 * 🔴 Der Standard ist der ERSTE Eintrag, wie die Verwaltung ihn definiert hat —
 *   nicht der erste sichtbare. Sonst wanderte die Rolle mit, sobald jemand
 *   umsortiert, und die Invariante schuetzt nichts mehr.
 */
export function istStandard(section, path) {
  return section?.items?.[0]?.path === path
}

/**
 * Darf dieser Eintrag versteckt werden?
 *
 * Die eine Regel, die den Bereich benutzbar haelt: der Standard nicht, und der
 * letzte verbleibende Sichtbare nicht.
 */
export function darfVerstecken(section, path, prefs) {
  if (istStandard(section, path)) return false
  const sichtbar = sichtbarePfade(section, prefs)
  return sichtbar.length > 1
}

/**
 * ALLE Eintraege eines Abschnitts in persoenlicher Reihenfolge — OHNE Ausblenden.
 *
 * Das ist die eine Quelle der Menge (W18): `wendeAn` und `sichtbarePfade` sind
 * beide darauf zurueckgefuehrt, damit es genau EINE Implementierung gibt. Zwei
 * Implementierungen derselben Menge laufen auseinander, sobald eine davon
 * angefasst wird — und der Unterschied faellt erst auf, wenn ein Eintrag fehlt.
 *
 * 🔴 Die Reihenfolge: erst die persoenliche Liste (falls vorhanden), dann der
 *   Rest in der Reihenfolge der Verwaltung. So bleibt ein NEUER Eintrag, den die
 *   Verwaltung spaeter hinzufuegt, sichtbar und am Ende — statt zu verschwinden,
 *   weil er in einer alten gespeicherten Reihenfolge nicht vorkommt.
 *
 * 🔴 NICHT auf den Favoriten-Abschnitt anwenden — er hat kein `items` (siehe
 *   Modul-Docstring, Satz 2).
 */
export function sortiereNachPrefs(section, prefs) {
  const eintraege = section?.items || []
  const persoenlich = prefs?.reihenfolge?.[section?.id]
  if (!Array.isArray(persoenlich) || persoenlich.length === 0) return eintraege
  const rang = new Map(persoenlich.map((p, i) => [p, i]))
  return [...eintraege].sort((a, b) => {
    const ra = rang.has(a.path) ? rang.get(a.path) : Number.MAX_SAFE_INTEGER
    const rb = rang.has(b.path) ? rang.get(b.path) : Number.MAX_SAFE_INTEGER
    return ra - rb
  })
}

/** Wendet die Voreinstellung auf die Eintraege eines Abschnitts an (ohne die versteckten). */
export function wendeAn(section, prefs) {
  const versteckt = new Set(prefs?.versteckt || [])
  return sortiereNachPrefs(section, prefs).filter(i => !versteckt.has(i.path))
}

/** Die sichtbaren PFADE eines Abschnitts, in persoenlicher Reihenfolge. */
export function sichtbarePfade(section, prefs) {
  const versteckt = new Set(prefs?.versteckt || [])
  return sortiereNachPrefs(section, prefs).filter(i => !versteckt.has(i.path)).map(i => i.path)
}

// ── Favoriten ────────────────────────────────────────────────────────────────

/** Steht dieser Pfad in der Favoritenliste? */
export function istFavorit(path, prefs) {
  return (prefs?.favoriten || []).includes(path)
}

/**
 * Der EINE Schreiber des Favoriten-Arrays.
 *
 * Jede Aenderung an `favoriten` laeuft hier durch — Anheften, Loesen, Umsortieren,
 * Zuruecksetzen. Das ist der Grund, warum es ueberhaupt eine Funktion ist: die
 * Reihenfolge IST das Array, also gibt es genau eine Stelle, an der es entsteht,
 * und genau eine, an der Dubletten und Nicht-Strings aussortiert werden.
 */
export function setzeFavoriten(pfade, prefs) {
  const sauber = [...new Set((Array.isArray(pfade) ? pfade : []).filter(p => typeof p === 'string'))]
  return { ...prefs, favoriten: sauber }
}

/**
 * Heftet einen Pfad an — idempotent, ans Ende.
 *
 * 🔴 Angeheftet wird eine KOPIE: der Eintrag bleibt in seinem Abschnitt stehen.
 *   Ein Umzug zwischen Abschnitten gibt es nicht (Plan §E4), und der Stern ist
 *   die einzige Art, ihn wieder loszuwerden.
 */
export function hefteAn(path, prefs) {
  if (istFavorit(path, prefs)) return prefs
  return setzeFavoriten([...(prefs?.favoriten || []), path], prefs)
}

/** Loest einen Pfad aus der Favoritenliste — ein Unbekannter aendert nichts. */
export function loeseFavorit(path, prefs) {
  if (!istFavorit(path, prefs)) return prefs
  return setzeFavoriten((prefs?.favoriten || []).filter(p => p !== path), prefs)
}

// ── Zug (Drag & Drop) ────────────────────────────────────────────────────────

/**
 * Verschiebt einen Eintrag INNERHALB seines Abschnitts auf `zielIndex`.
 *
 * Schreibt `reihenfolge[section.id]`. Ziel ausserhalb der Liste → unveraendert
 * (derselbe Randfall wie beim alten `verschiebe`).
 */
export function verschiebeInAbschnitt(section, path, zielIndex, prefs) {
  const pfade = sortiereNachPrefs(section, prefs).map(i => i.path)
  const von = pfade.indexOf(path)
  if (von < 0 || !Number.isInteger(zielIndex) || zielIndex < 0 || zielIndex > pfade.length) return prefs
  pfade.splice(von, 1)
  pfade.splice(Math.min(zielIndex, pfade.length), 0, path)
  return { ...prefs, reihenfolge: { ...prefs.reihenfolge, [section.id]: pfade } }
}

/**
 * Verschiebt einen Pfad INNERHALB der Favoritenliste auf `zielIndex`.
 *
 * Schreibt `favoriten[]` — ueber `setzeFavoriten`, den einen Schreiber.
 */
export function verschiebeInFavoriten(path, zielIndex, prefs) {
  const pfade = [...(prefs?.favoriten || [])]
  const von = pfade.indexOf(path)
  if (von < 0 || !Number.isInteger(zielIndex) || zielIndex < 0 || zielIndex > pfade.length) return prefs
  pfade.splice(von, 1)
  pfade.splice(Math.min(zielIndex, pfade.length), 0, path)
  return setzeFavoriten(pfade, prefs)
}

/**
 * Die reine Zug-Regel — sie beantwortet JEDEN Eingang, auch die unerreichbaren.
 *
 * Der Modus sperrt im DOM die Ziele, die hier „nichts" ergeben (`isDropDisabled`,
 * Plan §3.3): ein Zug ueber die Abschnittsgrenze landet gar nicht erst in einem
 * fremden Abschnitt. Die Regel ist trotzdem vollstaendig, weil sie eine reine
 * Funktion ist — und weil das DOM der zweite Riegel ist, nicht der erste.
 *
 * | von → nach | Ergebnis |
 * |---|---|
 * | Abschnitt → derselbe Abschnitt | `verschiebeInAbschnitt` |
 * | Abschnitt → anderer Abschnitt | nichts |
 * | Abschnitt → Favoriten | `hefteAn`, an der Zielposition |
 * | Favoriten → Favoriten | `verschiebeInFavoriten` |
 * | Favoriten → Abschnitt | nichts (kein Umzug zwischen Abschnitten) |
 * | kein Ziel (abgebrochen) | nichts |
 *
 * 🔴 Die siebte Zeile der Plantabelle („Favoriten-Abschnitt als Abschnitt
 *   verschoben") ist KEIN Eingang dieser Funktion: Abschnitte sind keine
 *   Draggables, es gibt fuer sie weder Griff noch `draggableId`.
 *
 * @param {object} prefs
 * @param {object} zug
 * @param {string} zug.pfad            der gezogene Eintrag
 * @param {object|null} zug.vonSection der Quell-Abschnitt — `null` heisst: aus den Favoriten
 * @param {string|null} zug.nachId     Ziel-Abschnitts-Id, `FAVORITEN`, oder `null` (kein Ziel)
 * @param {number} zug.index           Zielposition im Zielfeld
 */
export function wendeZugAn(prefs, zug) {
  const { pfad, vonSection = null, nachId = null, index = 0 } = zug || {}
  if (!pfad || !nachId) return prefs
  if (nachId === FAVORITEN) return verschiebeInFavoriten(pfad, index, hefteAn(pfad, prefs))
  if (!vonSection || vonSection.id !== nachId) return prefs
  return verschiebeInAbschnitt(vonSection, pfad, index, prefs)
}

// ── Ausblenden ───────────────────────────────────────────────────────────────

/** Versteckt einen Eintrag — oder nicht, wenn die Invariante es verbietet. */
export function verstecke(section, path, prefs) {
  if (!darfVerstecken(section, path, prefs)) return prefs
  return { ...prefs, versteckt: [...new Set([...prefs.versteckt, path])] }
}

/** Macht einen Eintrag wieder sichtbar. */
export function zeige(path, prefs) {
  return { ...prefs, versteckt: prefs.versteckt.filter(p => p !== path) }
}

/** Zurueck auf Anfang — der Weg heraus, den die Invariante offenlaesst. Raeumt Favoriten mit. */
export function zuruecksetzen() { return LEER }
